import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useFlowObserverPanelStore, FlowObserverPanelEngine } from '@flow-studio/flow-observer-core';
import type { PanelMode, FlowObserverPanelTabRuntimeState } from '@flow-studio/flow-observer-core';

interface TabBarProps {
  panelMode: PanelMode;
  onSetMode: (mode: PanelMode) => void;
}

// ── Sortable tab item ─────────────────────────────────────────────────────────

interface SortableTabProps {
  tab: FlowObserverPanelTabRuntimeState;
  isActive: boolean;
  label: string;
  icon?: React.ReactNode;
  onTabClick: (id: string) => void;
  onClose: (id: string) => void;
  onMoveLeft: (id: string) => void;
  onMoveRight: (id: string) => void;
  onRegisterRef: (id: string, el: HTMLDivElement | null) => void;
  isFirst: boolean;
  isLast: boolean;
}

const SortableTab: React.FC<SortableTabProps> = ({
  tab,
  isActive,
  label,
  icon,
  onTabClick,
  onClose,
  onMoveLeft,
  onMoveRight,
  onRegisterRef,
  isFirst,
  isLast,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tab.id });

  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      onRegisterRef(tab.id, el);
    },
    [setNodeRef, tab.id, onRegisterRef]
  );

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 999 : undefined,
  };

  const hasError = tab.loadState === 'error';
  const isLoading = tab.loadState === 'loading';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Delete') onClose(tab.id);
    if (e.key === 'Enter' || e.key === ' ') onTabClick(tab.id);
    if (e.ctrlKey && e.key === 'ArrowLeft') { e.preventDefault(); onMoveLeft(tab.id); }
    if (e.ctrlKey && e.key === 'ArrowRight') { e.preventDefault(); onMoveRight(tab.id); }
  };

  return (
    <div
      ref={combinedRef}
      style={style}
      {...attributes}
      {...listeners}
      role="tab"
      aria-selected={isActive}
      aria-controls={`fop-tabpanel-${tab.id}`}
      id={`fop-tab-${tab.id}`}
      className={[
        'fop-tab',
        isActive ? 'fop-tab--active' : '',
        hasError ? 'fop-tab--error' : '',
        isLoading ? 'fop-tab--loading' : '',
        isDragging ? 'fop-tab--dragging' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => onTabClick(tab.id)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      title={label}
      aria-label={`${label}${isActive ? ', selected' : ''}${!isFirst ? '. Ctrl+Left to move left' : ''}${!isLast ? '. Ctrl+Right to move right' : ''}`}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{label}</span>
      {tab.badge !== undefined && tab.badge !== 0 && (
        <span className="fop-tab__badge" aria-label={`${tab.badge} items`}>
          {tab.badge}
        </span>
      )}
      <button
        className="fop-tab__close"
        onClick={(e) => { e.stopPropagation(); onClose(tab.id); }}
        aria-label={`Close ${label}`}
        title={`Close ${label}`}
        tabIndex={-1}
      >
        ×
      </button>
    </div>
  );
};

// ── TabBar ────────────────────────────────────────────────────────────────────

export const TabBar: React.FC<TabBarProps> = ({ panelMode, onSetMode }) => {
  const tabRuntimeStates = useFlowObserverPanelStore((s) => s.tabRuntimeStates);
  const activeTabId = useFlowObserverPanelStore((s) => s.activeTabId);
  const registry = useFlowObserverPanelStore((s) => s.getRegistry());
  const setActiveTabId = useFlowObserverPanelStore((s) => s.setActiveTabId);
  const closeTab = useFlowObserverPanelStore((s) => s.closeTab);
  const openTab = useFlowObserverPanelStore((s) => s.openTab);
  const activateTab = useFlowObserverPanelStore((s) => s.activateTab);
  const reorderTab = useFlowObserverPanelStore((s) => s.reorderTab);

  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(false);
  const [overflowIds, setOverflowIds] = useState<string[]>([]);

  const addBtnRef = useRef<HTMLButtonElement>(null);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const overflowBtnRef = useRef<HTMLButtonElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);
  const tabsTrackRef = useRef<HTMLDivElement>(null);
  const tabRefsMap = useRef<Map<string, HTMLElement>>(new Map());

  const openTabs = useMemo(
    () =>
      Object.values(tabRuntimeStates)
        .filter((t) => t.userState === 'open' && t.engineState !== 'hidden')
        .sort((a, b) => a.order - b.order),
    [tabRuntimeStates]
  );

  const closedTabs = useMemo(
    () =>
      Object.values(tabRuntimeStates)
        .filter((t) => t.userState === 'closed' && t.engineState !== 'hidden')
        .sort((a, b) => a.order - b.order),
    [tabRuntimeStates]
  );

  // Save tab visibility state to localStorage whenever it changes
  useEffect(() => {
    try {
      const openTabIds = openTabs.map(t => t.id);
      localStorage.setItem('flowObserver.openTabs', JSON.stringify(openTabIds));
    } catch (e) {
      console.warn('Failed to save open tabs to localStorage', e);
    }
  }, [openTabs]);

  const handleTabClick = (id: string) => {
    const runtime = tabRuntimeStates[id];
    if (!runtime) return;
    // User-initiated tab pick — gates the auto-focus-execution-status behavior on WorkflowStarted (§5.1).
    FlowObserverPanelEngine.markUserSelectedTab();
    if (runtime.loadState === 'idle') {
      activateTab(id);
    } else {
      setActiveTabId(id);
    }
  };

  // ── Tab ref registration for overflow measurement ─────────────────────────

  const registerTabRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) {
      tabRefsMap.current.set(id, el);
    } else {
      tabRefsMap.current.delete(id);
    }
  }, []);

  // ── ResizeObserver: detect which tabs overflow the track ──────────────────

  const recomputeOverflow = useCallback(() => {
    const track = tabsTrackRef.current;
    if (!track) return;
    const trackRight = track.getBoundingClientRect().right;
    const ids: string[] = [];
    for (const tab of openTabs) {
      const el = tabRefsMap.current.get(tab.id);
      if (!el) continue;
      // A tab overflows when its right edge exceeds the track's right edge
      if (el.getBoundingClientRect().right > trackRight + 1) {
        ids.push(tab.id);
      }
    }
    setOverflowIds((prev) => {
      if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) return prev;
      return ids;
    });
  }, [openTabs]);

  useEffect(() => {
    const track = tabsTrackRef.current;
    if (!track) return;
    recomputeOverflow();
    const ro = new ResizeObserver(recomputeOverflow);
    ro.observe(track);
    return () => ro.disconnect();
  }, [recomputeOverflow]);

  // ── Reset overflow menu when it has no tabs to show ──────────────────────

  useEffect(() => {
    if (overflowIds.length === 0) setOverflowMenuOpen(false);
  }, [overflowIds.length]);

  // ── Close overflow menu on outside click ─────────────────────────────────

  useEffect(() => {
    if (!overflowMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        overflowBtnRef.current?.contains(e.target as Node) ||
        overflowMenuRef.current?.contains(e.target as Node)
      ) return;
      setOverflowMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [overflowMenuOpen]);

  // ── Close add menu on outside click ──────────────────────────────────────

  useEffect(() => {
    if (!addMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        addBtnRef.current?.contains(e.target as Node) ||
        addMenuRef.current?.contains(e.target as Node)
      ) return;
      setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [addMenuOpen]);

  // ── dnd-kit drag-to-reorder ───────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const currentStates = useFlowObserverPanelStore.getState().tabRuntimeStates;
    const sorted = Object.values(currentStates)
      .filter((t) => t.userState === 'open' && t.engineState !== 'hidden')
      .sort((a, b) => a.order - b.order);

    const fromIdx = sorted.findIndex((t) => t.id === active.id);
    const toIdx = sorted.findIndex((t) => t.id === over.id);
    if (fromIdx < 0 || toIdx < 0) return;

    // Insert-style reorder (matches dnd-kit's visual preview during drag)
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);

    // Assign the original order slot values to the new positions
    reordered.forEach((tab, idx) => {
      const targetOrder = sorted[idx].order;
      if (tab.order !== targetOrder) {
        reorderTab(tab.id, targetOrder);
      }
    });

    // Save tab order to localStorage
    try {
      const tabOrder = reordered.map(t => t.id);
      localStorage.setItem('flowObserver.tabOrder', JSON.stringify(tabOrder));
    } catch (e) {
      console.warn('Failed to save tab order to localStorage', e);
    }
  }, [reorderTab]);

  // ── Ctrl+Left / Ctrl+Right keyboard reorder ───────────────────────────────

  const handleMoveLeft = useCallback((id: string) => {
    const sorted = Object.values(useFlowObserverPanelStore.getState().tabRuntimeStates)
      .filter((t) => t.userState === 'open' && t.engineState !== 'hidden')
      .sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((t) => t.id === id);
    if (idx <= 0) return;
    const prev = sorted[idx - 1];
    const curr = sorted[idx];
    reorderTab(id, prev.order);
    reorderTab(prev.id, curr.order);
    // Save updated tab order
    const updated = sorted.map((t, i) => i === idx - 1 ? id : i === idx ? prev.id : t.id);
    try {
      localStorage.setItem('flowObserver.tabOrder', JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save tab order', e);
    }
  }, [reorderTab]);

  const handleMoveRight = useCallback((id: string) => {
    const sorted = Object.values(useFlowObserverPanelStore.getState().tabRuntimeStates)
      .filter((t) => t.userState === 'open' && t.engineState !== 'hidden')
      .sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((t) => t.id === id);
    if (idx < 0 || idx >= sorted.length - 1) return;
    const next = sorted[idx + 1];
    const curr = sorted[idx];
    reorderTab(id, next.order);
    reorderTab(next.id, curr.order);
    // Save updated tab order
    const updated = sorted.map((t, i) => i === idx ? next.id : i === idx + 1 ? id : t.id);
    try {
      localStorage.setItem('flowObserver.tabOrder', JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save tab order', e);
    }
  }, [reorderTab]);

  const isCollapsed = panelMode === 'collapsed';
  const tabIds = openTabs.map((t) => t.id);

  return (
    <div className="fop-tabbar" role="tablist" aria-label="FlowObserver Panels">
      {/* Scrollable tabs track — tabs that don't fit are clipped by overflow:hidden.
          role="presentation" keeps the tab/tablist ARIA ownership chain unbroken. */}
      <div ref={tabsTrackRef} className="fop-tabs-track" role="presentation">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
            {openTabs.map((tab, idx) => {
              const descriptor = registry.descriptors[tab.id];
              const label = descriptor?.fallbackLabel ?? tab.id;
              return (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  label={label}
                  icon={descriptor?.icon}
                  onTabClick={handleTabClick}
                  onClose={closeTab}
                  onMoveLeft={handleMoveLeft}
                  onMoveRight={handleMoveRight}
                  onRegisterRef={registerTabRef}
                  isFirst={idx === 0}
                  isLast={idx === openTabs.length - 1}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      {/* Overflow [»] button — only when tabs are clipped */}
      {overflowIds.length > 0 && (
        <div className="fop-overflow-container">
          <button
            ref={overflowBtnRef}
            className="fop-icon-btn fop-overflow-btn"
            title={`${overflowIds.length} more tab${overflowIds.length > 1 ? 's' : ''}`}
            aria-label={`${overflowIds.length} more tab${overflowIds.length > 1 ? 's' : ''}`}
            aria-haspopup="menu"
            aria-expanded={overflowMenuOpen}
            onClick={() => setOverflowMenuOpen((v) => !v)}
          >
            »
          </button>
          {overflowMenuOpen && (
            <div ref={overflowMenuRef} className="fop-dropdown fop-overflow-menu" role="menu">
              {overflowIds.map((id) => {
                const tab = tabRuntimeStates[id];
                const descriptor = registry.descriptors[id];
                const label = descriptor?.fallbackLabel ?? id;
                const isActive = id === activeTabId;
                return (
                  <button
                    key={id}
                    className={`fop-dropdown__item${isActive ? ' fop-dropdown__item--active' : ''}`}
                    role="menuitem"
                    onClick={() => { handleTabClick(id); setOverflowMenuOpen(false); }}
                  >
                    {descriptor?.icon && <span aria-hidden="true">{descriptor.icon}</span>}
                    <span>{label}</span>
                    {tab?.badge !== undefined && tab.badge !== 0 && (
                      <span className="fop-tab__badge" aria-label={`${tab.badge} items`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="fop-tabbar__controls">
        {/* Add Panel button */}
        <div className="fop-dropdown-container">
          <button
            ref={addBtnRef}
            className="fop-icon-btn"
            title="Add panel"
            aria-label="Add panel"
            onClick={() => setAddMenuOpen((v) => !v)}
          >
            +
          </button>
          {addMenuOpen && (
            <div ref={addMenuRef} className="fop-dropdown" role="menu">
              {closedTabs.length === 0 && (
                <div className="fop-dropdown__item fop-dropdown__item--disabled">
                  No panels available
                </div>
              )}
              {closedTabs.map((tab) => {
                const descriptor = registry.descriptors[tab.id];
                const label = descriptor?.fallbackLabel ?? tab.id;
                const isDisabled = tab.engineState === 'disabled';
                return (
                  <button
                    key={tab.id}
                    className={`fop-dropdown__item${isDisabled ? ' fop-dropdown__item--disabled' : ''}`}
                    role="menuitem"
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        openTab(tab.id);
                        setAddMenuOpen(false);
                      }
                    }}
                    title={isDisabled ? (descriptor?.disabledReasonKey ?? 'Not available') : undefined}
                  >
                    {descriptor?.icon && <span aria-hidden="true">{descriptor.icon}</span>}
                    <span>{label}</span>
                    {isDisabled && (
                      <span className="fop-dropdown__disabled-label">disabled</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Mode controls */}
        {panelMode !== 'fullscreen' && (
          <button className="fop-icon-btn" title="Fullscreen" aria-label="Enter fullscreen"
            onClick={() => onSetMode('fullscreen')}>⤢</button>
        )}
        {panelMode !== 'floating' && (
          <button className="fop-icon-btn" title="Float" aria-label="Float panel"
            onClick={() => onSetMode('floating')}>⧉</button>
        )}
        {isCollapsed ? (
          <button className="fop-icon-btn" title="Expand" aria-label="Expand panel"
            onClick={() => onSetMode('docked')}>↑</button>
        ) : (
          <button className="fop-icon-btn" title="Collapse" aria-label="Collapse panel"
            onClick={() => onSetMode('collapsed')}>↓</button>
        )}
        <button className="fop-icon-btn" title="Close panel" aria-label="Close panel"
          onClick={() => onSetMode('closed')}>×</button>
      </div>
    </div>
  );
};
